import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { orderService } from '../services/orderService';
import '../styles/SearchResults.css';

const SearchResults = ({ query, onClose, onSelect }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [results, setResults] = useState({
    type: null,
    items: [],
    loading: false,
  });
  const resultsRef = useRef(null);

  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults({ type: null, items: [], loading: false });
      return;
    }

    // Utiliser un petit délai pour éviter trop de recherches pendant la frappe
    const timeoutId = setTimeout(() => {
      const searchQuery = query.trim().toLowerCase();
      performSearch(searchQuery);
    }, 200); // Délai de 200ms

    return () => clearTimeout(timeoutId);
  }, [query, user]);

  const isUrl = (str) => {
    try {
      const url = new URL(str.startsWith('http') ? str : `https://${str}`);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const performSearch = async (searchQuery) => {
    try {
      setResults({ type: null, items: [], loading: true });

      const allResults = [];
      const originalQuery = query ? query.trim() : ''; // Utiliser la query originale (non lowercased)
      
      console.log('🔍 Recherche pour:', searchQuery, 'Query originale:', originalQuery);

      // 1. Vérifier si c'est un lien URL (priorité haute)
      if (originalQuery && (isUrl(originalQuery) || isUrl(`https://${originalQuery}`))) {
        const fullUrl = originalQuery.startsWith('http') ? originalQuery : `https://${originalQuery}`;
        allResults.push({
          type: 'product-link',
          icon: '🔗',
          title: 'Créer une commande avec ce lien',
          description: fullUrl.length > 60 ? fullUrl.substring(0, 60) + '...' : fullUrl,
          action: () => {
            navigate('/order', { state: { productLink: fullUrl } });
            onClose();
          },
        });
      }

      // 2. Recherche dans les catégories (toujours effectuée)
      const categoryResults = searchCategories(searchQuery);
      console.log('📁 Catégories trouvées:', categoryResults.length);
      allResults.push(...categoryResults);
      
      // 3. Recherche dans les plateformes
      const platformResults = searchPlatforms(searchQuery);
      console.log('🏪 Plateformes trouvées:', platformResults.length);
      allResults.push(...platformResults);

      // 4. Recherche dans les commandes (si connecté)
      if (user) {
        const orderResults = await searchOrders(searchQuery);
        allResults.push(...orderResults);
      }

      // 5. Suggestions de produits populaires
      const popularSuggestions = getPopularSuggestions(searchQuery);
      allResults.push(...popularSuggestions);

      // Déterminer le type de résultats
      let resultType = 'mixed';
      if (allResults.length === 1 && allResults[0].type === 'product-link') {
        resultType = 'link';
      }

      console.log('✅ Total résultats:', allResults.length, allResults);
      
      setResults({
        type: resultType,
        items: allResults.slice(0, 10), // Limiter à 10 résultats
        loading: false,
      });
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
      setResults({
        type: 'error',
        items: [],
        loading: false,
      });
    }
  };

  const searchCategories = (query) => {
    const categories = [
      { name: 'Mode & Accessoires', icon: '👗', keywords: ['mode', 'vêtement', 'fashion', 'accessoire', 'habillement', 'robe', 'chemise', 'pantalon', 'sac', 'bag'] },
      { name: 'Électronique', icon: '📱', keywords: ['électronique', 'phone', 'téléphone', 'smartphone', 'tablette', 'ordinateur', 'laptop', 'pc', 'iphone', 'samsung'] },
      { name: 'Maison & Jardin', icon: '🏠', keywords: ['maison', 'jardin', 'décoration', 'mobilier', 'home', 'meuble', 'déco', 'décoration'] },
      { name: 'Beauté', icon: '💄', keywords: ['beauté', 'cosmétique', 'maquillage', 'parfum', 'soin', 'makeup', 'beauty', 'crème'] },
      { name: 'Chaussures', icon: '👟', keywords: ['chaussure', 'sneaker', 'basket', 'botte', 'sandale', 'shoe', 'soulier', 'nike', 'adidas'] },
      { name: 'Sports & Loisirs', icon: '🎮', keywords: ['sport', 'loisir', 'jeu', 'fitness', 'gym', 'sportif', 'entraînement', 'football', 'basketball'] },
      { name: 'Bijoux & Montres', icon: '💍', keywords: ['bijou', 'montre', 'bague', 'collier', 'bracelet', 'jewelry', 'watch', 'ring'] },
      { name: 'Enfants & Jouets', icon: '🧸', keywords: ['enfant', 'jouet', 'bébé', 'toy', 'kid', 'children', 'baby'] },
    ];

    return categories
      .filter(cat => {
        const nameMatch = cat.name.toLowerCase().includes(query);
        const keywordMatch = cat.keywords.some(keyword => keyword.toLowerCase().includes(query));
        return nameMatch || keywordMatch;
      })
      .map(cat => ({
        type: 'category',
        icon: cat.icon,
        title: cat.name,
        description: `Parcourir la catégorie ${cat.name}`,
        action: () => {
          navigate('/categories');
          onClose();
        },
      }));
  };

  const searchPlatforms = (query) => {
    const platforms = [
      { name: 'Shein', icon: '🛍️', keywords: ['shein', 'shien', 'she'] },
      { name: 'Amazon', icon: '📦', keywords: ['amazon', 'amz'] },
      { name: 'Alibaba', icon: '🏭', keywords: ['alibaba', 'ali', 'baba'] },
      { name: 'AliExpress', icon: '🌐', keywords: ['aliexpress', 'ali express', 'express'] },
    ];

    return platforms
      .filter(platform => {
        const nameMatch = platform.name.toLowerCase().includes(query);
        const keywordMatch = platform.keywords.some(keyword => keyword.includes(query));
        return nameMatch || keywordMatch;
      })
      .map(platform => ({
        type: 'platform',
        icon: platform.icon,
        title: `Commander sur ${platform.name}`,
        description: `Créer une commande depuis ${platform.name}`,
        action: () => {
          navigate('/order', { state: { platform: platform.name } });
          onClose();
        },
      }));
  };

  const searchOrders = async (query) => {
    try {
      if (!user?.id) return [];

      const userId = user.id || localStorage.getItem('userId');
      const response = await orderService.getUserOrders(userId);

      if (!response.success || !response.orders) return [];

      const matchingOrders = response.orders
        .filter(order => {
          const searchLower = query.toLowerCase();
          return (
            order._id.toLowerCase().includes(searchLower) ||
            order.platform?.toLowerCase().includes(searchLower) ||
            order.trackingId?.toLowerCase().includes(searchLower) ||
            order.items?.some(item => 
              item.verifiedProductName?.toLowerCase().includes(searchLower) ||
              item.productLink?.toLowerCase().includes(searchLower)
            )
          );
        })
        .slice(0, 3) // Limiter à 3 commandes
        .map(order => ({
          type: 'order',
          icon: '📦',
          title: `Commande #${order._id.slice(-8).toUpperCase()}`,
          description: `${order.platform} - ${order.status}`,
          action: () => {
            navigate(`/tracking/${order._id}`);
            onClose();
          },
        }));

      return matchingOrders;
    } catch (error) {
      console.error('Erreur lors de la recherche dans les commandes:', error);
      return [];
    }
  };

  const getPopularSuggestions = (query) => {
    // Suggestions basées sur des termes populaires
    const popularTerms = [
      { term: 'robe', action: () => navigate('/categories') },
      { term: 'téléphone', action: () => navigate('/categories') },
      { term: 'chaussure', action: () => navigate('/categories') },
      { term: 'sac', action: () => navigate('/categories') },
    ];

    return popularTerms
      .filter(item => item.term.includes(query))
      .slice(0, 2)
      .map(item => ({
        type: 'suggestion',
        icon: '💡',
        title: `Rechercher "${item.term}"`,
        description: 'Parcourir les produits',
        action: () => {
          item.action();
          onClose();
        },
      }));
  };

  if (!query || query.trim().length < 2) {
    if (results.loading) {
      return (
        <div className="search-results" ref={resultsRef}>
          <div className="search-results-loading">
            <div className="loading-spinner"></div>
            <span>Recherche en cours...</span>
          </div>
        </div>
      );
    }
    return null;
  }

  if (results.loading) {
    return (
      <div className="search-results" ref={resultsRef}>
        <div className="search-results-loading">
          <div className="loading-spinner"></div>
          <span>Recherche en cours...</span>
        </div>
      </div>
    );
  }

  if (results.items.length === 0 && !results.loading) {
    return (
      <div className="search-results" ref={resultsRef}>
        <div className="search-results-empty">
          <p>Aucun résultat trouvé pour "{query}"</p>
          <button
            className="search-all-results"
            onClick={() => {
              navigate('/categories');
              onClose();
            }}
          >
            Parcourir toutes les catégories
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="search-results" ref={resultsRef}>
      <div className="search-results-header">
        <span className="results-count">
          {results.items.length} résultat{results.items.length > 1 ? 's' : ''}
        </span>
      </div>
      <div className="search-results-list">
        {results.items.map((item, index) => (
          <div
            key={index}
            className="search-result-item"
            onClick={item.action}
          >
            <div className="result-icon">{item.icon}</div>
            <div className="result-content">
              <div className="result-title">{item.title}</div>
              <div className="result-description">{item.description}</div>
            </div>
            <div className="result-arrow">→</div>
          </div>
        ))}
      </div>
      {query && !isUrl(query) && (
        <div className="search-results-footer">
          <button
            className="search-all-results"
            onClick={() => {
              navigate('/categories');
              onClose();
            }}
          >
            Voir tous les résultats pour "{query}"
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchResults;

